import { Component } from '@theme/component';

export class BbdProductModel extends Component {
  #frame = 0;
  #isDragging = false;
  #lastX = 0;
  #animationFrame;
  #frameCount = 72; // total frames across all strips
  #stripCount = 3; // sprite is split into this many vertical strips
  #framesPerStrip = 24; // frames per strip (derived from frameCount / stripCount)
  #deferredStripsLoaded = false; // strips 2..n load on first interaction
  #dragAccumulator = 0; // tracks fractional movement between frames
  #pixelsPerFrame = 5; // lower = faster rotation; baseline tuned for responsiveness
  #activePointerId = null;

  // Inertia state
  #velocity = 0; // px per ms (smoothed)
  #lastTs = 0; // last timestamp from pointer/inertia
  #inertiaRafId = null;
  #reducedMotion = false;
  #lastMoveTs = 0; // last timestamp we received a real pointermove
  #lastInstV = 0; // last instantaneous velocity at move (px/ms)

  // Tuning knobs
  #velocitySmooth = 0.3; // 0..1 low-pass filter alpha
  #maxVelocity = 1.2; // px per ms cap to avoid wild flicks (~2000px/s)
  #minVelocity = 0.1; // px per ms threshold to stop inertia (~100 px/s)
  #minReleaseVelocity = 0.12; // px per ms required at release to start inertia (~120 px/s)
  #idleWindowMs = 100; // only allow inertia if release occurs within this window after the last move
  #decayPerFrame = 0.94; // velocity multiplier per 16.7ms frame (time-corrected)
  #dragDirection = -1; // 1 = original, -1 = inverted (drag left rotates right)

  // Intro animation (initial 45° left rotation on first load)
  #introRafId = null;
  #introDone = false;
  #introDurationMs = 1000;
  #introDelayMs = 600; // delay after image ready before starting intro
  #introTimeoutId = null;

  connectedCallback() {
    super.connectedCallback();

    // Store settings
    this.#frameCount = parseInt(this.dataset.frameCount) || this.#frameCount;
    this.#stripCount = parseInt(this.dataset.stripCount) || this.#stripCount;
    this.#framesPerStrip = Math.ceil(this.#frameCount / this.#stripCount);

    // Start on the last frame (last strip) instead of frame 0 (first strip).
    // The sprite is a circular 360° sweep, so the last frame is only one step
    // away from frame 0 visually, but it lets the intro rotation stay entirely
    // within the last strip — the only strip that's eager-loaded — so the
    // first and middle strips can be fully deferred until interaction.
    this.#frame = this.#frameCount - 1;

    // Respect user preference for reduced motion
    this.#reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Prevent native scrolling/gesture interference on the grab handle
    if (this.refs?.grab) this.refs.grab.style.touchAction = 'none';

    // Calculate adaptive sensitivity from viewer width and frame count
    this.#recalculateSensitivity();

    // Setup unified Pointer Events listeners with stable references for cleanup
    this._onPointerDown = (e) => this.#onPointerDown(e);
    this._onPointerMove = (e) => this.#onPointerMove(e);
    this._onPointerUp = () => this.#onPointerUp();
    this._onResize = () => this.#recalculateSensitivity();

    if (this.refs?.grab) this.refs.grab.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
    window.addEventListener('resize', this._onResize);

    this.#updateFrame();

    // Play initial rotation only after the sprite image has fully loaded/decoded
    this.#scheduleIntroAfterImageReady();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#animationFrame);
    if (this.#inertiaRafId != null) {
      cancelAnimationFrame(this.#inertiaRafId);
      this.#inertiaRafId = null;
    }
    if (this.#introRafId != null) {
      cancelAnimationFrame(this.#introRafId);
      this.#introRafId = null;
    }
    if (this.#introTimeoutId != null) {
      clearTimeout(this.#introTimeoutId);
      this.#introTimeoutId = null;
    }

    // Cleanup listeners
    if (this.refs?.grab && this._onPointerDown) this.refs.grab.removeEventListener('pointerdown', this._onPointerDown);
    if (this._onPointerMove) window.removeEventListener('pointermove', this._onPointerMove);
    if (this._onPointerUp) {
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
    }
    if (this._onResize) window.removeEventListener('resize', this._onResize);

    // Cleanup pending image load listener if any (attached to the last strip)
    const images = this.refs?.images;
    const lastImg = images?.[images.length - 1];
    if (this._onImageLoad && lastImg) {
      lastImg.removeEventListener('load', this._onImageLoad);
      this._onImageLoad = null;
    }
  }

  // ---------- Interaction Handlers ----------

  #onPointerDown(e) {
    // Fallback: if the user grabs during the intro animation (which cancels it
    // before it can finish), the intro-complete trigger never fires, so load
    // the deferred strips here. Idempotent via the #deferredStripsLoaded guard.
    this.#loadDeferredStrips();

    this.#isDragging = true;
    this.#lastX = e.clientX;
    this.#lastTs = e.timeStamp || performance.now();
    this.#lastMoveTs = this.#lastTs;
    this.#lastInstV = 0;
    this.#activePointerId = e.pointerId;

    // Cancel the intro animation/timeout if it's running
    if (this.#introTimeoutId != null) {
      clearTimeout(this.#introTimeoutId);
      this.#introTimeoutId = null;
    }
    if (this.#introRafId != null) {
      cancelAnimationFrame(this.#introRafId);
      this.#introRafId = null;
    }
    this.#introDone = true;

    // Cancel any ongoing inertia when a new drag starts
    if (this.#inertiaRafId != null) {
      cancelAnimationFrame(this.#inertiaRafId);
      this.#inertiaRafId = null;
    }
    this.#velocity = 0;

    // Capture the pointer so we continue to receive move/up events even if it leaves the element
    try {
      this.refs?.grab?.setPointerCapture?.(e.pointerId);
    } catch (err) {
      /* noop: pointer capture may fail on some browsers */
    }
  }

  #onPointerUp() {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    if (this.#activePointerId != null) {
      try {
        this.refs?.grab?.releasePointerCapture?.(this.#activePointerId);
      } catch (err) {
        /* noop: ignore if capture not set */
      }
    }
    this.#activePointerId = null;

    // Decide whether to start inertia:
    // Only if release happens shortly after the last move (still moving)
    // AND instantaneous release velocity is above a threshold,
    // AND smoothed velocity is meaningful, AND reduced motion is off.
    const now = performance.now();
    const idleFor = now - (this.#lastMoveTs || now);
    const movingOnRelease = idleFor <= this.#idleWindowMs && Math.abs(this.#lastInstV) >= this.#minReleaseVelocity;

    if (!this.#reducedMotion && movingOnRelease && Math.abs(this.#velocity) > this.#minVelocity) {
      this.#startInertia();
    } else {
      this.#dragAccumulator = 0;
      this.#velocity = 0;
    }
  }

  #onPointerMove(e) {
    if (!this.#isDragging) return;
    if (this.#activePointerId != null && e.pointerId !== this.#activePointerId) return;

    const now = e.timeStamp || performance.now();
    const dt = Math.max(1, now - this.#lastTs); // ms, avoid div-by-zero
    const delta = e.clientX - this.#lastX;

    // Update instantaneous and smoothed velocity (px/ms)
    const instV = delta / dt;
    this.#lastInstV = instV;
    const alpha = this.#velocitySmooth;
    const nextV = (1 - alpha) * this.#velocity + alpha * instV;
    // Clamp velocity to avoid wild spins
    this.#velocity = Math.max(-this.#maxVelocity, Math.min(this.#maxVelocity, nextV));

    // Apply movement to frames immediately during drag
    this.#handleDrag(delta);

    // Update last positions/timestamps
    this.#lastX = e.clientX;
    this.#lastTs = now;
    this.#lastMoveTs = now;
  }

  // ---------- Core Animation Logic ----------

  // Wait until the sprite image is fully loaded/decoded before playing the intro
  #scheduleIntroAfterImageReady() {
    if (this.#introDone) return;
    // The intro plays entirely within the last strip (see connectedCallback),
    // so wait for that strip to decode before playing it. This avoids a blank
    // flash mid-spin on slower connections.
    const images = this.refs?.images;
    const img = images?.[images.length - 1];
    const scheduleIntro = () => {
      if (this.#introDone) return;
      if (this.#introTimeoutId != null) {
        clearTimeout(this.#introTimeoutId);
        this.#introTimeoutId = null;
      }
      this.#introTimeoutId = setTimeout(() => {
        this.#introTimeoutId = null;
        if (!this.#introDone) this.#playInitialRotation();
      }, this.#introDelayMs);
    };

    if (!img) {
      // If we don't have the image ref for some reason, just attempt to schedule after delay
      scheduleIntro();
      return;
    }

    // If already loaded, optionally ensure decode completes to avoid flashes
    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      if (typeof img.decode === 'function') {
        img
          .decode()
          .catch(() => {})
          .finally(scheduleIntro);
      } else {
        scheduleIntro();
      }
      return;
    }

    // Otherwise, wait for load once
    this._onImageLoad = () => {
      // Cleanup the listener immediately
      img.removeEventListener('load', this._onImageLoad);
      this._onImageLoad = null;
      const el = img;
      if (el && typeof el.decode === 'function') {
        el.decode()
          .catch(() => {})
          .finally(scheduleIntro);
      } else {
        scheduleIntro();
      }
    };
    img.addEventListener('load', this._onImageLoad, { once: true });
  }

  // Play an initial 45° left rotation once on first load
  #playInitialRotation() {
    if (this.#introDone) return;
    this.#introDone = true; // ensure it only runs once per mount

    // Rotate ~45° backward (negative steps) starting from the last frame, so
    // every frame touched by the intro stays within the last strip.
    const targetSteps = -Math.max(1, Math.round((this.#frameCount * 45) / 360));

    const applyFrame = (steps) => {
      this.#frame = (this.#frame + steps + this.#frameCount) % this.#frameCount;
      this.#updateFrame();
    };

    // If reduced motion is requested, jump directly without animation
    if (this.#reducedMotion) {
      applyFrame(targetSteps);
      return;
    }

    // Animate over a short duration with ease-out
    const duration = this.#introDurationMs;
    const startTs = performance.now();
    let lastApplied = 0;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (ts) => {
      // Abort if user starts interacting or refs are gone
      if (this.#isDragging || !this.refs?.images?.length) {
        if (this.#introRafId != null) cancelAnimationFrame(this.#introRafId);
        this.#introRafId = null;
        return;
      }

      const elapsed = ts - startTs;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const totalStepsNow = Math.round(eased * targetSteps);
      const deltaSteps = totalStepsNow - lastApplied;
      if (deltaSteps !== 0) {
        applyFrame(deltaSteps);
        lastApplied = totalStepsNow;
      }

      if (t >= 1) {
        this.#introRafId = null;
        return;
      }
      this.#introRafId = requestAnimationFrame(tick);
    };

    this.#introRafId = requestAnimationFrame(tick);
  }

  #handleDrag(delta) {
    // Accumulate drag distance and convert to integer frame steps
    this.#dragAccumulator += delta * this.#dragDirection; // apply direction multiplier
    const steps = Math.trunc(this.#dragAccumulator / this.#pixelsPerFrame);
    if (steps !== 0) {
      this.#frame = (this.#frame + steps + this.#frameCount) % this.#frameCount;
      this.#updateFrame();
      // keep remainder to preserve sub-frame movement
      this.#dragAccumulator -= steps * this.#pixelsPerFrame;
    }
  }

  // Inertia animation loop
  #startInertia() {
    if (this.#inertiaRafId != null) cancelAnimationFrame(this.#inertiaRafId);
    this.#lastTs = performance.now();
    const tick = (ts) => this.#tickInertia(ts);
    this.#inertiaRafId = requestAnimationFrame(tick);
  }

  #tickInertia(ts) {
    // Stop if user resumed dragging or refs missing
    if (this.#isDragging || !this.refs?.viewer || !this.refs?.images?.length) {
      if (this.#inertiaRafId != null) cancelAnimationFrame(this.#inertiaRafId);
      this.#inertiaRafId = null;
      this.#velocity = 0;
      this.#dragAccumulator = 0;
      return;
    }

    const dt = Math.max(1, (ts || performance.now()) - (this.#lastTs || performance.now())); // ms
    this.#lastTs = ts || performance.now();

    // Advance frames based on current velocity
    const deltaPixels = this.#velocity * dt; // px
    if (deltaPixels !== 0) this.#handleDrag(deltaPixels);

    // Time-corrected exponential decay of velocity
    const baseFrameMs = 16.6667;
    const factor = Math.pow(this.#decayPerFrame, dt / baseFrameMs);
    this.#velocity *= factor;

    // Stop when velocity is negligible
    if (Math.abs(this.#velocity) <= this.#minVelocity) {
      if (this.#inertiaRafId != null) cancelAnimationFrame(this.#inertiaRafId);
      this.#inertiaRafId = null;
      this.#velocity = 0;
      this.#dragAccumulator = 0;
      return;
    }

    this.#inertiaRafId = requestAnimationFrame((t) => this.#tickInertia(t));
  }

  #recalculateSensitivity() {
    const vw = this.refs?.viewer?.offsetWidth;
    // Goal: one full 360° rotation after dragging 3× the viewer width
    // pixelsPerFrame = (3 * viewerWidth) / frameCount
    if (!vw || !this.#frameCount) {
      // Fallback to a small but sane default if refs not ready
      this.#pixelsPerFrame = Math.max(0.25, this.#pixelsPerFrame || 1);
      return;
    }
    const pixelsPerFullRotation = 3 * vw;
    const ppf = pixelsPerFullRotation / this.#frameCount;
    // Allow fractional pixels-per-frame for smoother stepping; clamp to avoid stalls on tiny viewports
    this.#pixelsPerFrame = Math.max(0.25, ppf);
  }

  // Promote the deferred strips' data-* sources to real ones so the browser
  // starts fetching them. Runs once, on the first pointer interaction.
  #loadDeferredStrips() {
    if (this.#deferredStripsLoaded) return;
    this.#deferredStripsLoaded = true;

    for (const img of this.refs?.images || []) {
      const source = img.parentElement?.querySelector('source[data-srcset]');
      if (source) {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    }
  }

  #updateFrame() {
    const images = this.refs?.images;
    if (!images || !images.length) return;

    // Which strip holds the current frame, and the frame's index within that strip.
    const fps = this.#framesPerStrip;
    const stripIndex = Math.min(images.length - 1, Math.floor(this.#frame / fps));
    const localFrame = this.#frame - stripIndex * fps;

    // Percentage-based translation, relative to each strip image's own height.
    const stepPct = 100 / fps;
    const offsetPct = -localFrame * stepPct;

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      if (i === stripIndex) {
        img.style.transform = `translateY(${offsetPct}%)`;
        img.classList.add('is-active');
      } else {
        img.classList.remove('is-active');
      }
    }
  }
}

if (!customElements.get('bbd-product-model')) {
  customElements.define('bbd-product-model', BbdProductModel);
}
